import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Package, Calendar, User } from 'lucide-react';

interface OrderSuccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  packageName: string;
  vendorName: string;
  eventName: string;
  totalAmount: number;
}

const OrderSuccessModal: React.FC<OrderSuccessModalProps> = ({
  open,
  onOpenChange,
  orderId,
  packageName,
  vendorName,
  eventName,
  totalAmount,
}) => {
  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <DialogTitle>Order Confirmed!</DialogTitle>
          <DialogDescription>
            Your video order has been successfully placed
          </DialogDescription>
        </DialogHeader>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">Order ID</p>
              <p className="font-mono text-sm bg-muted px-2 py-1 rounded">
                {orderId.substring(0, 8)}...
              </p>
            </div>

            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center gap-3">
                <Package className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">{packageName}</p>
                  <p className="text-sm text-muted-foreground">{formatPrice(totalAmount)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">{vendorName}</p>
                  <p className="text-sm text-muted-foreground">Video vendor</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">{eventName}</p>
                  <p className="text-sm text-muted-foreground">Event</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-blue-900">What's Next?</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• The vendor will contact you with video details</li>
                <li>• You'll receive updates on your video production</li>
                <li>• Payment processing will be handled separately</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Button onClick={() => onOpenChange(false)} className="w-full">
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default OrderSuccessModal;